/**
 * First-time curator onboarding intent helpers.
 * Intent is locked server-side after first selection.
 * Feature is controlled by REACT_APP_ONBOARDING_INTENT_V1 (default: enabled unless explicitly 'false').
 */

import axios from 'axios';
import { BASE_URL } from '../constants/api-urls';
import { getAuth0AuthHeaders } from './auth0Session';

export type OnboardingIntent = 'submissions' | 'tools' | 'both';

export const POST_SPOTIFY_REDIRECT_KEY = 'postSpotifyRedirect';
export const CAMPAIGNS_ONBOARDING_PATH = '/campaigns?onboarding=1';

export const isOnboardingIntentFeatureEnabled = (): boolean => {
  const flag = process.env.REACT_APP_ONBOARDING_INTENT_V1;
  // Default ON so the new flow works; set to 'false' to force legacy path.
  return flag !== 'false' && flag !== '0';
};

export const normalizeOnboardingIntent = (
  value: unknown
): OnboardingIntent | null => {
  if (value === null || value === undefined || value === '') return null;
  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'submissions' || normalized === 'tools' || normalized === 'both') {
    return normalized;
  }
  return null;
};

/**
 * Show intent screen only for first-time users with no plan and no intent yet.
 * Legacy users (free / paid / already intent) never see it.
 */
export const shouldShowIntentScreen = (args: {
  free?: boolean | null;
  hasActiveOrTrialingPlan?: boolean;
  onboardingIntent?: OnboardingIntent | string | null;
  expireStatus?: string | null;
  featureEnabled?: boolean;
}): boolean => {
  const featureEnabled =
    args.featureEnabled !== undefined
      ? args.featureEnabled
      : isOnboardingIntentFeatureEnabled();

  if (!featureEnabled) return false;
  if (args.hasActiveOrTrialingPlan) return false;
  if (args.free === true) return false;
  if (normalizeOnboardingIntent(args.onboardingIntent)) return false;

  // Payment recovery UI should not show intent
  const expire = (args.expireStatus || '').toLowerCase();
  if (expire && expire !== 'loading' && expire !== '') {
    // Only block for known bad payment states when used that way
    if (['past_due', 'unpaid', 'canceled', 'cancelled', 'incomplete', 'incomplete_expired'].includes(expire)) {
      return false;
    }
  }

  return true;
};

/**
 * campaignsOnboardingRequired === true  → still required
 * campaignsOnboardingRequired === false → completed (do not re-force from intent alone)
 * unknown/null → fall back to intent A/C only for mid-flow recovery
 */
export const requiresCampaignsOnboarding = (
  intent?: OnboardingIntent | string | null,
  campaignsOnboardingRequired?: boolean | null
): boolean => {
  if (campaignsOnboardingRequired === true) return true;
  if (campaignsOnboardingRequired === false) return false;
  const normalized = normalizeOnboardingIntent(intent);
  return normalized === 'submissions' || normalized === 'both';
};

/**
 * Where to send the user after plan completion (/created) or free A completion.
 * tools → home; submissions/both → campaigns onboarding.
 * Missing intent (legacy) → home.
 */
export const getPostPlanRedirectPath = (
  intent?: OnboardingIntent | string | null,
  campaignsOnboardingRequired?: boolean | null
): string => {
  if (!isOnboardingIntentFeatureEnabled()) {
    return '/home';
  }
  if (requiresCampaignsOnboarding(intent, campaignsOnboardingRequired)) {
    return CAMPAIGNS_ONBOARDING_PATH;
  }
  return '/home';
};

/**
 * After /shift hydrates a user who already has free or paid access,
 * choose landing path. Prefer home for fully onboarded users.
 */
export const getShiftLandingPath = (args: {
  free?: boolean | null;
  hasActiveOrTrialingPlan?: boolean;
  onboardingIntent?: OnboardingIntent | string | null;
  campaignsOnboardingRequired?: boolean | null;
}): string => {
  if (!isOnboardingIntentFeatureEnabled()) {
    return '/home';
  }

  // Only force campaigns when still required
  if (args.campaignsOnboardingRequired === true) {
    return CAMPAIGNS_ONBOARDING_PATH;
  }

  // If flag was never cleared but intent is A/C and we cannot know offer state here,
  // prefer home for returning free/paid users to avoid trapping them.
  // Campaigns page itself can still guide if they open it.
  return '/home';
};

export const setPostSpotifyRedirect = (path: string) => {
  try {
    localStorage.setItem(POST_SPOTIFY_REDIRECT_KEY, path);
  } catch {
    // ignore storage failures
  }
};

export const consumePostSpotifyRedirect = (fallback = '/home'): string => {
  try {
    const stored = localStorage.getItem(POST_SPOTIFY_REDIRECT_KEY);
    if (stored) {
      localStorage.removeItem(POST_SPOTIFY_REDIRECT_KEY);
      // Safety: only allow internal paths
      if (typeof stored === 'string' && stored.startsWith('/') && !stored.startsWith('//')) {
        return stored;
      }
    }
  } catch {
    // ignore
  }
  return fallback;
};

export const peekPostSpotifyRedirect = (): string | null => {
  try {
    const stored = localStorage.getItem(POST_SPOTIFY_REDIRECT_KEY);
    if (stored && stored.startsWith('/') && !stored.startsWith('//')) {
      return stored;
    }
  } catch {
    // ignore
  }
  return null;
};

export const INTENT_OPTION_COPY = {
  submissions: {
    value: 'submissions' as OnboardingIntent,
    title: 'Earn revenue from submissions',
    description:
      'Get campaign submissions from artists and earn from placements. We will set you up on the Free plan and take you to campaigns setup.',
  },
  tools: {
    value: 'tools' as OnboardingIntent,
    title: 'Professional curation tools',
    description:
      'Use playlist tools, scheduling, and professional curation features. You will choose a plan next, then go to your dashboard.',
  },
  both: {
    value: 'both' as OnboardingIntent,
    title: 'Both',
    description:
      'Use curation tools and receive submissions. You will choose a plan, then complete campaigns setup (Spotify + curator offer).',
  },
} as const;

export const markCampaignsOnboardingComplete = async (): Promise<void> => {
  try {
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    const userId = userInfo?.userId;
    if (userId) {
      await axios.post(
        `${BASE_URL}/auth0/user/${encodeURIComponent(userId)}/campaigns-onboarding/complete`,
        {},
        { headers: getAuth0AuthHeaders() }
      );
    }
    localStorage.setItem(
      'userInfo',
      JSON.stringify({ ...userInfo, campaignsOnboardingRequired: false })
    );
  } catch (error) {
    console.error('Failed to mark campaigns onboarding complete:', error);
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      localStorage.setItem(
        'userInfo',
        JSON.stringify({ ...userInfo, campaignsOnboardingRequired: false })
      );
    } catch {
      // ignore
    }
  }
};

export const isCampaignsOnboardingRequired = (): boolean => {
  try {
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    return userInfo?.campaignsOnboardingRequired === true;
  } catch {
    return false;
  }
};
